import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import Header from "components/common/Header/Header";
import SideBarNew from "components/common/SideBar/SideBarNew";
import SelectOption from "components/common/Select/Select";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { employeeTypeOption } from "Constants/constant";
import {
  REPORTING_BREADCRUMB,
  REPORTING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import { LoginContext } from "helpers/LoginContext";
import { addMonths, baseURLImage, getNameProfile } from "Utils/utils";
import { apiPostRequest } from "helpers/Requests";
import { toast } from "react-toastify";
import { getHRMTeamManagmentRequest } from "store/actions/sp.actions";
import { getTeamManagmentList } from "store/selector/sp.selector";
import { URL_REPORTING_XERO_TIMESHEET_EXPORT_REPORT } from "store/services/URL";
import useReportExport from "../useReportExport";
import {
  ReportFilterActions,
  ReportFilterField,
  ReportFilterGrid,
  ReportFiltersShell,
  ReportPageContent,
  ReportTableSection,
} from "../reportFilterUi";

/** title = full label for tooltip; lines = one or two rows in the header cell */
const COLUMN_DEFS = [
  { key: "name", title: "Name", lines: ["Name"] },
  { key: "total_hrs", title: "Total Hours", lines: ["Total", "Hours"] },
  { key: "day", title: "Day", lines: ["Day"] },
  { key: "evening", title: "Evening", lines: ["Evening"] },
  { key: "night", title: "Night", lines: ["Night"] },
  { key: "saturday", title: "Saturday", lines: ["Saturday"] },
  { key: "sunday", title: "Sunday", lines: ["Sunday"] },
  { key: "ph", title: "Public Holiday", lines: ["Public", "Holiday"] },
  { key: "sleep", title: "Sleepover", lines: ["Sleepover"] },
  { key: "ot15", title: "Overtime 1.5x", lines: ["OT", "1.5×"] },
  { key: "ot2", title: "Overtime 2x", lines: ["OT", "2x"] },
  { key: "wot15", title: "Weekly Overtime 1.5x", lines: ["Wkly OT", "1.5×"] },
  { key: "wot2", title: "Weekly Overtime 2x", lines: ["Wkly OT", "2x"] },
  { key: "km", title: "KM's", lines: ["KM's"] },
];

/** Same URL rules as AsyncMultiSelectDropdown / worker avatars: absolute URLs as-is, else IMAGE_URL + path. */
const getWorkerAvatarSrc = (rawPhoto, firstName, lastName) => {
  const pic = String(rawPhoto || "").trim();
  if (pic !== "") {
    return /^https?:\/\//i.test(pic) ? pic : baseURLImage(pic);
  }
  const first = (firstName || "").trim() || " ";
  const last = (lastName || "").trim() || " ";
  return getNameProfile(first, last) || getNameProfile("?", "?");
};

const XeroTimesheetExportReports = () => {
  const dispatch = useDispatch();
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const teamManagementList = useSelector(getTeamManagmentList);

  const handleImageError = (e, fname, lname) => {
    e.target.src =
      getNameProfile(fname || " ", lname || " ") || getNameProfile("?", "?");
  };

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedEmployeeTypes, setSelectedEmployeeTypes] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [isDisableSearch, setIsDisableSearch] = useState(false);
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const { runExport, QueuedModal } = useReportExport(
    "xero_timesheet_report",
    "Payroll Report"
  );

  useEffect(() => {
    dispatch(
      getHRMTeamManagmentRequest({
        filters: { page: 1, per_page: 500, search: null },
        bodyFilter: null,
      })
    );
  }, [dispatch]);

  const workerOptions = useMemo(() => {
    const all = teamManagementList?.employees?.data ?? [];
    const typeSet = new Set(
      (selectedEmployeeTypes || []).map((t) => t.value)
    );
    const list =
      typeSet.size > 0
        ? all.filter(
            (item) =>
              item?.employee_type &&
              String(item.employee_type).trim() !== "" &&
              typeSet.has(String(item.employee_type).trim())
          )
        : all;
    const workers = list.map((item) => ({
      label: `${item?.first_name || ""} ${item?.last_name || ""}`.trim(),
      value: item?.id,
    }));
    return workers.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }, [teamManagementList, selectedEmployeeTypes]);

  useEffect(() => {
    setSelectedWorkers((prev) => {
      if (!prev?.length) {
        return prev;
      }
      const valid = new Set(workerOptions.map((w) => w.value));
      const next = prev.filter((s) => valid.has(s.value));
      if (next.length === prev.length) {
        return prev;
      }
      return next;
    });
  }, [workerOptions]);

  const workersById = useMemo(() => {
    const map = new Map();
    for (const emp of teamManagementList?.employees?.data ?? []) {
      if (emp?.id != null) {
        map.set(Number(emp.id), emp);
      }
    }
    return map;
  }, [teamManagementList]);

  const fetchReport = async (reportType = null) => {
    if (!fromDate || !toDate) {
      toast("Select date range to get data");
      return;
    }
    setLoader(true);
    const payload = {
      timesheet_date_range: [fromDate, toDate],
      workers:
        selectedWorkers && selectedWorkers.length > 0
          ? selectedWorkers.map((w) => w.value)
          : [],
    };
    if (selectedEmployeeTypes && selectedEmployeeTypes.length > 0) {
      payload.employee_types = selectedEmployeeTypes.map((t) => t.value);
    }
    if (reportType) {
      await runExport({
        format: reportType,
        payload,
        syncUrl: URL_REPORTING_XERO_TIMESHEET_EXPORT_REPORT,
        totalRows: rows?.length,
        setLoader,
        onSyncExcel: (downloadUrl) => window.open(downloadUrl, "_blank"),
        onSyncPdf: (downloadUrl) => window.open(downloadUrl, "_blank"),
      });
      return;
    }
    try {
      const res = await apiPostRequest(
        URL_REPORTING_XERO_TIMESHEET_EXPORT_REPORT,
        payload
      );
      if (res?.status === 200) {
        setRows(res?.data?.data?.xero_timesheet_exports || []);
      }
    } catch (error) {
      toast.error(`${error?.message || error}`);
    } finally {
      setLoader(false);
    }
  };

  const handleClearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
    setSelectedEmployeeTypes([]);
    setSelectedWorkers([]);
    setRows([]);
  };

  return (
    <div className='min-h-full'>
      <SideBarNew />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col`}
      >
        <main className='flex-1 bg-gray-50 min-h-screen'>
          <Header
            title='Payroll Report'
            breadcrumb={
              <Breadcrumb
                pages={REPORTING_BREADCRUMB(
                  REPORTING_BREADCRUMB_PAGES.XERO_TIMESHEET_EXPORT_REPORTS
                )}
              />
            }
          />

          <ReportPageContent>
            <ReportFiltersShell>
              <ReportFilterGrid columns={3}>
                <ReportFilterField
                  label="Date Range (Max 1 Month)"
                  required
                >
                  <DateRangePickerNew
                    handleCancel={() => {
                      setFromDate(null);
                      setToDate(null);
                      setRows([]);
                    }}
                    handleChange={(obj) => {
                      if (obj?.fromDate && obj?.toDate) {
                        setFromDate(obj.fromDate);
                        setToDate(obj.toDate);
                      }
                    }}
                    setSelectedFromDate={setSelectedFromDate}
                    fromDate={fromDate}
                    toDate={toDate}
                    maxDate={addMonths(selectedFromDate, 1)}
                    setIsDisableSearch={setIsDisableSearch}
                  />
                </ReportFilterField>
                <ReportFilterField label="Employee Type">
                  <SelectOption
                    newDesign
                    cssClass="w-full"
                    isMulti
                    isSearch
                    data={employeeTypeOption}
                    selectedOption={selectedEmployeeTypes}
                    handleChange={(e) => setSelectedEmployeeTypes(e || [])}
                  />
                </ReportFilterField>
                <ReportFilterField label="Workers">
                  <SelectOption
                    newDesign
                    cssClass="w-full"
                    isMulti
                    isSearch
                    data={workerOptions}
                    selectedOption={selectedWorkers}
                    handleChange={(e) => setSelectedWorkers(e || [])}
                  />
                </ReportFilterField>
              </ReportFilterGrid>
              <ReportFilterActions
                onSearch={() => fetchReport()}
                onClear={handleClearFilters}
                onExport={(e) => fetchReport(e)}
                isLoading={loader}
                isSearchDisabled={isDisableSearch}
                isUserGracePeriodOver={isUserGracePeriodOver}
              />
            </ReportFiltersShell>

            <ReportTableSection className="overflow-x-hidden">
              <div className='w-full max-w-full bg-white shadow-md ring-1 ring-black ring-opacity-5 md:rounded-lg'>
                <table className='w-full table-fixed min-w-full border border-gray-200 divide-y divide-gray-200'>
                  <thead className='sticky top-0 z-[5] shadow-[0_1px_0_0_rgb(229,231,235)]'>
                    <tr className='bg-blue-50'>
                      {COLUMN_DEFS.map((col, colIdx) => (
                        <th
                          key={col.key}
                          scope='col'
                          title={col.title}
                          className={
                            colIdx === 0
                              ? "border-b border-gray-200 bg-blue-50 pl-4 pr-3 py-3.5 text-left align-middle min-w-0 w-[22%]"
                              : "border-b border-gray-200 bg-blue-50 px-1.5 sm:px-2 py-3.5 text-center align-middle min-w-0"
                          }
                        >
                          <div
                            className={`flex flex-col justify-center gap-0.5 min-h-[2.5rem] ${
                              colIdx === 0 ? "items-start" : "items-center"
                            }`}
                          >
                            {col.lines.map((line, lineIdx) => (
                              <span
                                key={lineIdx}
                                className={`block max-w-full break-words leading-snug ${
                                  colIdx === 0 ? "text-left w-full" : "text-center w-full"
                                } ${
                                  lineIdx === 0
                                    ? "text-xs font-semibold text-gray-800"
                                    : "text-[11px] font-medium text-gray-600"
                                }`}
                              >
                                {line}
                              </span>
                            ))}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 bg-white'>
                    {loader ? (
                      <tr>
                        <td colSpan={COLUMN_DEFS.length} className='py-6 text-center'>
                          <CircularLoader
                            classes='flex justify-center items-center'
                            classloader='loader-l'
                          />
                        </td>
                      </tr>
                    ) : rows?.length > 0 ? (
                      rows.map((row, idx) => {
                        const wid =
                          row.worker_id != null ? Number(row.worker_id) : null;
                        const emp =
                          wid != null ? workersById.get(wid) : null;
                        const first =
                          row.first_name ?? emp?.first_name ?? "";
                        const last =
                          row.last_name ?? emp?.last_name ?? "";
                        const displayName =
                          (row.name && String(row.name).trim()) ||
                          `${first} ${last}`.trim();
                        const rawPhoto =
                          row.photo ??
                          row.profile_picture ??
                          emp?.photo ??
                          emp?.profile_picture ??
                          "";
                        const avatarSrc = getWorkerAvatarSrc(
                          rawPhoto,
                          first,
                          last
                        );
                        return (
                          <tr
                            key={
                              row.worker_id != null
                                ? `w-${row.worker_id}-${idx}`
                                : `xero-export-${idx}`
                            }
                            className='even:bg-white odd:bg-gray-50'
                          >
                            <td className='py-4 px-3 text-sm text-gray-700 sm:pl-6 min-w-0'>
                              <div className='flex items-center min-w-0'>
                                <div className='h-10 w-10 flex-shrink-0'>
                                  <img
                                    onError={(e) =>
                                      handleImageError(
                                        e,
                                        first,
                                        last || " "
                                      )
                                    }
                                    className='capitalize object-cover h-10 w-10 rounded-full'
                                    src={avatarSrc}
                                    alt=''
                                  />
                                </div>
                                <div className='ml-4 min-w-0 flex-1 capitalize'>
                                  <span className='break-words text-gray-900'>
                                    {displayName || "—"}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.fortnightly_hrs}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.daytime}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.evening}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.night}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.saturday}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.sunday}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.public_holiday}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.sleep_over}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.overtime_1_5x}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.overtime_2x}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.weekly_overtime_1_5x}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.weekly_overtime_2x}
                            </td>
                            <td className='px-2 py-4 text-sm text-center tabular-nums text-gray-700 min-w-0'>
                              {row.km}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={COLUMN_DEFS.length}
                          className='py-6 text-center text-red-600'
                        >
                          No data found. Please adjust your filters to get relevant results
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </ReportTableSection>
          </ReportPageContent>
        </main>
      </div>
      <QueuedModal />
    </div>
  );
};

export default XeroTimesheetExportReports;

